import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { fetchDriverById, fetchDrivers } from '../../redux/Drivers/actions';
import { parseDateCreated } from '../../helpers/utils/parser';
import {
  Box,
  Stack,
  Paper,
  Button,
  Typography,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '../../ui/core';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { cardHeaderSx, hairline, filterPillSx } from '../../ui/styles';
import ExpandMoreIcon from '../../ui/icons/ExpandMoreIcon';
import ChevronLeftIcon from '../../ui/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../ui/icons/ChevronRightIcon';
import DriverInfoForm from '../../components/widgets/DriversApproval/DriverInfoForm';
import DriverDocumentsForm from '../../components/widgets/DriversApproval/DriverDocumentsForm';
import DriverStatusField from '../../components/widgets/DriversApproval/DriverStatusField';
import DriverTypeField from '../../components/widgets/DriversApproval/DriverTypeField';
import DriverCityField from '../../components/widgets/DriversApproval/DriverCityField';
import DriverScreeningLink from '../../components/widgets/DriversApproval/DriverScreeningLink';
import { useHasPermission } from '../../helpers/api/auth/index';
import api from '../../lib/api';
import { useToast } from '../../hooks';
import AddVehicleDialog from '../../components/widgets/DriverVehicles/AddVehicleDialog';

// Matches the triage list's page size, so a cold-loaded profile (e.g. after a
// refresh) can repopulate its neighbours for the prev/next arrows.
const PAGE_SIZE = 25;
// Documents are read by hand to scan a license/insurance/registration, so the
// viewer is sized to fill most of the viewport height.
// The preview must fit inside the scrolling documents panel so viewer.js's
// zoom/rotate toolbar — which it overlays at the bottom edge of this box — stays
// on screen instead of falling below the fold. The subtraction accounts for the
// chrome stacked around it within a full-height (100vh) funnel: the card header
// (~73px), the content row padding (~40px), the "Documents" accordion summary
// (~48px), the tabs row plus its margin (~72px), the accordion detail padding
// (~16px), and the pinned prev/next footer (~59px) — ~308px, rounded up for slack.
const DOC_VIEWER_HEIGHT = 'calc(100vh - 330px)';

// The profile lives inside the shared surface card (from DriversApproval/index),
// so it mirrors the rest of the Intercom-style admin: a hairline card header, a
// body of soft hairline panels, and a pinned footer that always shows the
// prev/next controls no matter how tall the content gets.
const style = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    ...cardHeaderSx,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
    rowGap: '10px',
  },
  name: {
    fontSize: '19px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'text.primary',
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline' },
  },
  content: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: '16px',
    px: { xs: '22px', md: '40px' },
    pt: { xs: '18px', md: '24px' },
    pb: '16px',
    overflow: 'hidden',
  },
  // The applicant's editable fields — the secondary column.
  formPanel: {
    flex: { xs: 'none', md: '4 1 0' },
    width: '100%',
    minWidth: 0,
    minHeight: { xs: '360px', md: 0 },
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
    border: (theme) => `1px solid ${hairline(theme)}`,
    borderRadius: '12px',
    backgroundColor: 'background.paper',
    overflow: 'hidden',
  },
  // Documents — the primary column, wider and taller for scanning scans.
  documentsPanel: {
    flex: { xs: 'none', md: '6 1 0' },
    width: '100%',
    minWidth: 0,
    minHeight: { xs: 'auto', md: 0 },
    alignSelf: 'stretch',
    px: '24px',
    border: (theme) => `1px solid ${hairline(theme)}`,
    borderRadius: '12px',
    backgroundColor: 'background.paper',
    overflowY: 'auto',
  },
  footer: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    px: { xs: '22px', md: '40px' },
    py: '12px',
    borderTop: (theme) => `1px solid ${hairline(theme)}`,
  },
  navPill: {
    ...filterPillSx,
    maxWidth: 240,
    '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0 },
  },
};

const denseFormTheme = (outer) =>
  createTheme(outer, {
    components: {
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiAccordionSummary: { styleOverrides: { content: { margin: '12px 0' } } },
    },
  });

const fullName = (d) => `${d?.firstName || ''} ${d?.lastName || ''}`.trim();

const ApprovalFunnel = () => {
  const rootRef = useRef();
  const history = useHistory();
  const driver = useSelector((state) => state.drivers.selectedDriver);
  const drivers = useSelector((state) => state.drivers.all.data);
  const { id } = useParams();
  const isDriverLoaded = !!(driver && driver.id);
  const dispatch = useDispatch();
  const toast = useToast();
  const hasViewDriverPermission = useHasPermission('view_driver');

  const [section, setSection] = useState(undefined);
  const [docUploads, setDocUploads] = useState({ files: {} });
  const [addingVehicle, setAddingVehicle] = useState(false);

  // Prev/next within the currently loaded triage page, so a reviewer can move
  // straight to the adjacent applicant instead of going Back to the list.
  const currentIdx = drivers.findIndex((d) => d.driver_id === driver.driver_id);
  const prevDriver = currentIdx > 0 ? drivers[currentIdx - 1] : undefined;
  const nextDriver = currentIdx >= 0 ? drivers[currentIdx + 1] : undefined;

  const goToDriver = (d) => d && history.push(`/drivers-approval/funnel/${d.driver_id}`);

  const addVehicle = async (input) => {
    try {
      await api.driverProfile.addVehicle(driver.driver_id, input);
      await fetchDriverById(id)(dispatch);
      toast.success('Vehicle added');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not add that vehicle');
      return false;
    }
  };

  useEffect(() => {
    fetchDriverById(id)(dispatch);
  }, [id, dispatch]);

  // If the profile was opened cold (no triage list in memory — e.g. a refresh or
  // a deep link), pull the driver's own status page so the prev/next arrows have
  // neighbours to move between instead of disappearing.
  useEffect(() => {
    if (isDriverLoaded && drivers.length === 0 && driver.driverApplicationStatus) {
      fetchDrivers({ offset: 0, limit: PAGE_SIZE, driverApplicationStatus: driver.driverApplicationStatus })(dispatch);
    }
  }, [isDriverLoaded, drivers.length, driver.driverApplicationStatus, dispatch]);

  // Left/right arrow keys jump between applicants — but only when the reviewer
  // isn't typing into one of the form fields.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const el = e.target;
      const tag = (el.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable) return;
      if (e.key === 'ArrowLeft' && prevDriver) {
        history.push(`/drivers-approval/funnel/${prevDriver.driver_id}`);
      } else if (e.key === 'ArrowRight' && nextDriver) {
        history.push(`/drivers-approval/funnel/${nextDriver.driver_id}`);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [prevDriver, nextDriver, history]);

  if (!isDriverLoaded) {
    return (
      <Stack sx={{ ...style.root, alignItems: 'center' }}>
        <LinearProgress sx={{ width: '300px', marginTop: '300px' }} />
      </Stack>
    );
  }

  const driverEverApproved = driver.driverApplicationStatusUpdates.some((item) => item.status === 'approved');

  if (!hasViewDriverPermission) {
    return <>Missing view_driver permission.</>;
  }

  return (
    <ThemeProvider theme={denseFormTheme}>
      <Box ref={rootRef} sx={style.root}>
        <Box sx={style.header}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexWrap: 'wrap', rowGap: '8px', minWidth: 0 }}>
            <Box component="a" href={`/users/${driver.id}`} sx={style.name}>
              {`${driver.firstName || ''} ${driver.lastName || ''}`}
            </Box>
            <DriverTypeField driver={driver} />
            <DriverCityField driver={driver} />
            <DriverStatusField driver={driver} />
            <DriverScreeningLink driver={driver} />
          </Stack>
          <Typography sx={{ whiteSpace: 'nowrap' }} color="text.secondary">
            {(driver.isDriverVerified || driverEverApproved) && <i>Verified</i>}&nbsp; Applied{' '}
            {parseDateCreated(driver.driver_id)}
          </Typography>
        </Box>

        <Box sx={style.content}>
          <Paper elevation={0} sx={style.formPanel}>
            <DriverInfoForm
              key={`${driver.driver_id}:${driver.vehicles.length}`}
              driver={driver}
              uploads={docUploads}
              setUploads={setDocUploads}
              onSectionFocus={setSection}
              onAddVehicle={() => setAddingVehicle(true)}
            />
          </Paper>
          <Paper elevation={0} sx={style.documentsPanel}>
            <Accordion elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} tabIndex={-1}>
                <Typography variant="h2">Documents</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '0 0 16px 0' }}>
                <DriverDocumentsForm
                  section={section}
                  driver={driver}
                  uploads={docUploads}
                  setUploads={setDocUploads}
                  imageHeight={DOC_VIEWER_HEIGHT}
                />
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Box>

        <Box sx={style.footer}>
          <Button
            sx={style.navPill}
            disabled={!prevDriver}
            onClick={() => goToDriver(prevDriver)}
            startIcon={<ChevronLeftIcon sx={{ fontSize: 18 }} />}
            aria-label="Previous driver"
          >
            <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
              {prevDriver ? fullName(prevDriver) : 'Previous'}
            </Typography>
          </Button>
          <Button
            sx={style.navPill}
            disabled={!nextDriver}
            onClick={() => goToDriver(nextDriver)}
            endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
            aria-label="Next driver"
          >
            <Typography noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
              {nextDriver ? fullName(nextDriver) : 'Next'}
            </Typography>
          </Button>
        </Box>
        <AddVehicleDialog
          open={addingVehicle}
          onClose={() => setAddingVehicle(false)}
          onAdd={addVehicle}
          busy={false}
        />
      </Box>
    </ThemeProvider>
  );
};

export default ApprovalFunnel;
